import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, LayoutDashboard, LogOut, Pencil, Plus, Trash2, Upload, Users, Trophy, Loader2 } from "lucide-react";
import api, { formatApiError, setStoredClient } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ClientLogo from "../../components/ClientLogo";
import ClientFormDialog from "./ClientFormDialog";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

function ClientRow({ c, onEdit, onDelete, onEnter, onLogo, uploading }) {
  const fileRef = useRef(null);
  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5 flex flex-col md:flex-row md:items-center gap-5 hover:ring-blue-300 transition-shadow" data-testid={`client-row-${c.access_code}`}>
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <ClientLogo client={c} className="h-14 w-14 ring-1 ring-slate-200 text-lg shrink-0" testId={`client-logo-${c.access_code}`} />
        <div className="min-w-0">
          <div className="font-display text-lg font-semibold text-slate-900 truncate" data-testid={`client-name-${c.access_code}`}>{c.name}</div>
          <div className="text-sm text-slate-500 truncate">{c.title}</div>
          <div className="mt-1 inline-flex items-center rounded-md bg-slate-900 text-white text-xs font-mono tracking-widest px-2 py-0.5" data-testid={`client-code-${c.access_code}`}>{c.access_code}</div>
        </div>
      </div>
      <div className="flex gap-5 text-sm text-slate-600">
        <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{c.users_count}</span>
        <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4" />{c.competitions_count}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) onLogo(c, e.target.files[0]); e.target.value = ""; }}
          data-testid={`client-logo-input-${c.access_code}`} />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`client-logo-btn-${c.access_code}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Logo
        </Button>
        <Button variant="outline" size="sm" onClick={() => onEdit(c)} data-testid={`client-edit-btn-${c.access_code}`}>
          <Pencil className="h-4 w-4 mr-1.5" /> Modifica
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDelete(c)} className="hover:text-rose-600 hover:border-rose-300" data-testid={`client-delete-btn-${c.access_code}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button size="sm" onClick={() => onEnter(c)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`client-enter-btn-${c.access_code}`}>
          <LayoutDashboard className="h-4 w-4 mr-1.5" /> Pannello Amministratore
        </Button>
      </div>
    </div>
  );
}

export default function SuperadminClients() {
  const { logout, setSession } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onEnter = async (c) => {
    try {
      const { data } = await api.post(`/clients/${c.id}/enter`);
      setSession(data);
      setStoredClient(data.user.client);
      navigate("/admin");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onLogo = async (c, file) => {
    setUploadingId(c.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${c.id}/logo`, fd);
      toast.success("Logo aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploadingId(null); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/clients/${toDelete.id}`);
      toast.success("Cliente eliminato");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="superadmin-page">
      <header className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/lmkt.png" alt="LMKT" className="h-11 w-11 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-bold">Dance Analysis System</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Superadmin</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="superadmin-logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Gestione Clienti</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-2">Clienti</h1>
            <p className="text-slate-500 mt-3 max-w-xl">Ogni cliente ha il suo codice di accesso, il suo logo e utenti e competizioni separati.</p>
          </div>
          <Button onClick={() => setEditing({})} className="bg-blue-700 hover:bg-blue-800 text-white h-11" data-testid="add-client-button">
            <Plus className="h-4 w-4 mr-2" /> Nuovo cliente
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Caricamento…</div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500">
            <Building2 className="h-8 w-8 mx-auto mb-3" /> Nessun cliente
          </div>
        ) : (
          <div className="space-y-4" data-testid="clients-list">
            {clients.map((c) => (
              <ClientRow key={c.id} c={c} onEdit={setEditing} onDelete={setToDelete} onEnter={onEnter} onLogo={onLogo} uploading={uploadingId === c.id} />
            ))}
          </div>
        )}
      </main>

      <ClientFormDialog client={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati definitivamente anche tutti gli utenti ({toDelete?.users_count}) e le competizioni ({toDelete?.competitions_count}) di questo cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-client-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-client-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
